import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ChevronLeft, Plus, Edit2, Home, Briefcase } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import toast from 'react-hot-toast';
import LocateViaLiveMap from '../components/LocateViaLiveMap';

const MyAddresses = () => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [coordinates, setCoordinates] = useState(null);

  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    addressType: 'home'
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      if (user) {
        setAddresses(user.address ? [{ ...formData, address: user.address, pincode: user.pincode }] : []);
      }
    } catch (error) {
      toast.error('Failed to fetch addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Map widget fills these fields; we adapt Checkout-style keys -> MyAddresses formData keys.
  const handleMapFormDataChange = (updater) => {
    setFormData((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return {
        ...prev,
        // LocateViaLiveMap autofills street/city/state/zipCode/country.
        // MyAddresses expects: address/city/state/pincode.
        address: next.street ?? prev.address,
        city: next.city ?? prev.city,
        state: next.state ?? prev.state,
        pincode: next.zipCode ?? prev.pincode
        // phone/addressType remain untouched.
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const addressString = `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`;

      const updateData = {
        address: addressString,
        pincode: formData.pincode,
        phone: formData.phone
      };

      await userService.updateProfile(updateData);
      toast.success('Address saved successfully!');
      setShowForm(false);
      setEditingAddress(null);
      setCoordinates(null);
      setFormData({
        address: '',
        city: '',
        state: '',
        pincode: '',
        phone: '',
        addressType: 'home'
      });

      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save address');
    }
  };

  const handleEdit = (address, index) => {
    setEditingAddress(index);
    const parts = address.address.split(',').map((s) => s.trim());
    setFormData({
      address: parts[0] || '',
      city: parts[1] || '',
      state: parts[2]?.split('-')[0]?.trim() || '',
      pincode: address.pincode || '',
      phone: address.phone || '',
      addressType: 'home'
    });
    setCoordinates(null);
    setShowForm(true);
  };

  const getAddressIcon = (type) => {
    switch (type) {
      case 'work':
        return <Briefcase size={18} />;
      case 'home':
      default:
        return <Home size={18} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-2xl font-bold text-purple-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/store" className="p-2 hover:bg-white/20 rounded-lg">
              <ChevronLeft size={24} />
            </Link>
            <MapPin size={28} />
            <h1 className="text-xl font-bold">My Addresses</h1>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingAddress(null);
              setCoordinates(null);
              setFormData({
                address: '',
                city: '',
                state: '',
                pincode: '',
                phone: '',
                addressType: 'home'
              });
            }}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
          >
            <Plus size={20} />
            <span className="text-sm">Add New</span>
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* Address Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              {editingAddress !== null ? 'Edit Address' : 'Add New Address'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Address Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address Type</label>
                <div className="flex gap-4">
                  {['home', 'work'].map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="addressType"
                        value={type}
                        checked={formData.addressType === type}
                        onChange={handleInputChange}
                        className="text-purple-600"
                      />
                      <span className="text-sm capitalize">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Reusable locate widget */}
              <LocateViaLiveMap
                title="Address (Locate via Live Map)"
                formData={{
                  street: formData.address,
                  city: formData.city,
                  state: formData.state,
                  zipCode: formData.pincode
                }}
                onFormDataChange={handleMapFormDataChange}
                coordinates={coordinates}
                onCoordinatesChange={setCoordinates}
                locateButtonText="Locate via Live Map"
              />

              {/* Street Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="House No., Building Name, Street Name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  required
                />
              </div>

              {/* City & State */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="State"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>
              </div>

              {/* Pincode & Phone */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    placeholder="6-digit PIN"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Mobile Number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingAddress(null);
                    setCoordinates(null);
                  }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all"
                >
                  Save Address
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Address List */}
        {addresses.length === 0 && !showForm ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <MapPin size={64} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No addresses saved</h2>
            <p className="text-gray-500 mb-4">Add an address to make checkout easier.</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Add Address
            </button>
          </div>
        ) : (
          addresses.map((address, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex justify-between items-start">
                <div className="flex gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600">
                    {getAddressIcon(address.addressType)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800 capitalize">{address.addressType || 'Home'}</span>
                      {index === 0 && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full">Default</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{address.address}</p>
                    <p className="text-sm text-gray-500">{address.pincode}</p>
                    {address.phone && <p className="text-sm text-gray-500 mt-1">Phone: {address.phone}</p>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(address, index)}
                    className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MyAddresses;

